import { useEffect, useMemo, useState } from "react";
import {
  fetchMarketCatalog,
  fetchMarketPrices,
  type MarketCatalog,
  type MarketPriceItem,
} from "@/api/market";
import type { AppTab } from "@/modules/shell/tabs";
import { useSession } from "@/session/SessionContext";
import { fmtUsd, SPOT_PAIR_HINT_KEY } from "@/lib/format";

type QuickCard = {
  key: string;
  symbol: string;
  network: string;
  price: string;
  tone: "up" | "down" | "flat";
  pairHint: string;
};

const SERVICES: { id: AppTab | "irr"; label: string; ico: string; badge?: string }[] = [
  { id: "wallets", label: "واریز", ico: "↓" },
  { id: "wallets", label: "برداشت", ico: "↑" },
  { id: "trade", label: "اسپات", ico: "⇄" },
  { id: "oracle", label: "استخر", ico: "◎", badge: "نانوم" },
  { id: "shops", label: "شاپ", ico: "▣" },
  { id: "pay", label: "پرداخت", ico: "⌁" },
  { id: "market", label: "بازار", ico: "◈" },
  { id: "inbox", label: "صندوق", ico: "✉" },
];

export function HomePanel({ onJump }: { onJump: (t: AppTab) => void }) {
  const { session } = useSession();
  const [catalog, setCatalog] = useState<MarketCatalog | null>(null);
  const [prices, setPrices] = useState<MarketPriceItem[]>([]);

  useEffect(() => {
    fetchMarketCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
    const load = () =>
      fetchMarketPrices()
        .then((items) => setPrices(items || []))
        .catch(() => setPrices([]));
    void load();
    const t = window.setInterval(() => void load(), 12000);
    return () => clearInterval(t);
  }, []);

  const quick: QuickCard[] = useMemo(() => {
    const tokens = catalog?.tokens || [];
    const prefer = ["USDT", "NAG", "NOCT", "NUSD", "BNB", "BTC", "ETH"];
    const map = new Map(prices.map((p) => [`${p.networkCode}:${p.symbol}`, p]));
    const ranked = [...tokens].sort((a, b) => {
      const ia = prefer.indexOf(a.symbol.toUpperCase());
      const ib = prefer.indexOf(b.symbol.toUpperCase());
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    return ranked.slice(0, 6).map((t) => {
      const p = map.get(`${t.networkCode}:${t.symbol}`);
      return {
        key: `${t.networkCode}:${t.symbol}`,
        symbol: t.symbol,
        network: t.networkCode,
        price: fmtUsd(p?.priceUsd),
        tone: "flat" as const,
        pairHint: `${t.symbol.toUpperCase()}_USDT`,
      };
    });
  }, [catalog, prices]);

  return (
    <section className="panel home-panel wx-home" dir="rtl">
      <div className="wx-home-head">
        <div className="wx-brand-hero">
          <span className="wx-brand-name lg">NANOM</span>
          <span className="status-line">
            {session ? session.username || session.displayName || "on" : "—"}
          </span>
        </div>
      </div>

      {quick.length > 0 && (
        <div className="wx-section">
          <div className="wx-section-title">خرید آنی</div>
          <div className="wx-quick-scroll">
            {quick.map((c) => (
              <button
                key={c.key}
                type="button"
                className="wx-quick-card"
                onClick={() => {
                  try {
                    sessionStorage.setItem(SPOT_PAIR_HINT_KEY, c.pairHint);
                  } catch {
                    /* ignore */
                  }
                  onJump("trade");
                }}
              >
                <span className="wx-token-dot">{c.symbol.slice(0, 1)}</span>
                <b>{c.symbol}</b>
                <span className="muted">{c.network}</span>
                <span className={`ltr wx-px ${c.tone}`}>{c.price}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button type="button" className="wx-hero-banner" onClick={() => onJump("trade")}>
        <div>
          <b>معامله اسپات</b>
          <span className="muted">NAG · NOCT · NUSD</span>
        </div>
        <span className="wx-hero-cta">ورود</span>
      </button>

      <div className="wx-section">
        <div className="wx-service-grid">
          {SERVICES.map((s, idx) => (
            <button
              key={`${s.label}-${idx}`}
              type="button"
              className="wx-service"
              onClick={() => {
                if (s.id === "irr") onJump("wallets");
                else onJump(s.id);
              }}
            >
              {s.badge ? <span className="wx-badge">{s.badge}</span> : null}
              <span className="wx-service-ico" aria-hidden="true">
                {s.ico}
              </span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
